import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useLocalstorageState } from 'rooks';

import { cn } from '@/helpers/shadcn';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/app/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/ui/popover';
import { Button } from '@/app/ui/button';
import { useCompanies } from '@/data/company';
import { useSelf } from '@/data/user';

// Zebelers pick which company account they are viewing. The active company is
// held in the `active-company-uid` localStorage key and a BroadcastChannel tells
// other tabs to reload.
const CompanySwitcher = () => {
  const companies = useCompanies();
  const user = useSelf(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeCompanyUid, setActiveCompanyUid] = useLocalstorageState('active-company-uid', null);
  const [refreshChannel] = useState(() => new BroadcastChannel('refresh_channel'));

  // Reload every other tab when the active company changes so they stay in sync.
  refreshChannel.onmessage = (event) => {
    if (event.data === 'refresh') {
      window.location.href = '/dashboard';
    }
  };

  // Until the admin company list is available, just show the Zebel logo.
  if ((companies.isLoading && !companies.data) || companies.error) {
    return (
      <Link to="/" data-testid="nav-zebel-logo" className="flex size-11 items-center justify-center rounded-xl hover:bg-accent transition-colors">
        <img src="/assets/logos/zebel/icon.png" alt="Zebel" className="size-10 rounded-xl" />
      </Link>
    );
  }

  const companyUid = activeCompanyUid || user.data.company;
  const activeCompany = companies.data.content[companyUid];
  // Warn Zebelers when they are viewing a company that isn't their own.
  const isForeignCompany = companyUid !== user.data.company;

  const handleSelectCompany = (uid) => {
    // Persist the new active company, tell other tabs to reload, then hard-reload
    // this tab too (the broadcast only reaches other tabs) so every data hook
    // refetches under the newly selected company.
    setActiveCompanyUid(uid);
    refreshChannel.postMessage('refresh');
    window.location.href = '/dashboard';
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" data-testid="company-menu-trigger" className="relative size-11 rounded-xl p-0 hover:bg-accent">
          <img src="/assets/logos/zebel/icon.png" alt="Zebel" className="size-10 rounded-xl" />
          <span
            data-testid={isForeignCompany ? 'company-switcher-foreign-indicator' : undefined}
            className={cn(
              'absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-md border shadow-sm',
              isForeignCompany ? 'border-warning bg-warning text-warning-foreground' : 'bg-background text-muted-foreground'
            )}
          >
            <ChevronDown className="size-2.5" />
          </span>
          {isForeignCompany && <span className="sr-only">You are viewing a company that isn&apos;t your own</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={12} className="w-72 overflow-hidden p-0">
        <div className="px-3 py-3">
          <div className="truncate text-sm font-semibold" data-testid="company-menu-active-company">
            {activeCompany.company_name}
          </div>
          <div className="text-xs text-muted-foreground">Zebeler account</div>
        </div>
        <Command data-testid="company-switcher-list" className="rounded-none border-t">
          <CommandInput placeholder="Search companies..." />
          <CommandList className="max-h-60">
            <CommandEmpty>No companies found.</CommandEmpty>
            <CommandGroup>
              {companies.data.query.map((uid) => {
                const item = companies.data.content[uid];
                return (
                  <CommandItem key={uid} value={item.company_name} onSelect={() => handleSelectCompany(uid)} data-testid={`company-switcher-item-${item.company_name}`}>
                    <span className="truncate">{item.company_name}</span>
                    {uid === companyUid && <Check className="ml-auto size-4 text-primary" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CompanySwitcher;
